import React, { useMemo } from 'react';
import { Badge, Icons } from '~/lib/bn-material-ui';
import {
  useAccountFeatures,
  useStore,
  useSirenEntity,
  sirenActions,
  BinIcon,
  MillBinIcon,
  InsightsIcon,
  BarnIcon,
} from '~/utility/BinSentry-ui-utility';
import { subscriptionTypeDetails } from '~/notifications/subscription-entity-type.ts';
import { useMillBinsGroup } from '~/inventory/use-mill-bins-group';
import { useFilteredIssues } from '~/issues/use-filtered-issues';
import { useIssuesFilters } from '~/issues/use-issues-filters';

function EventsBadge({ issuesHref }) {
  const { entity: issuesEntity } = useSirenEntity({ href: issuesHref });

  const listAction = issuesEntity?.getAction('list-issues');
  const parsedAction = listAction ? sirenActions.parseAction(listAction, {
    limit: 100,
    sortBy: 'duration',
  }) : null;

  const listHref = parsedAction?.url?.href;

  const [ filters ] = useIssuesFilters();
  const { hideSnoozed } = filters;

  const { entity: issuesListEntity } = useSirenEntity({ href: listHref });
  const issues = useMemo(() => issuesListEntity?.entities || [], [issuesListEntity?.entities]);
  const { loading: loadingFiltered, filteredIssues } = useFilteredIssues(issues, hideSnoozed);
  const issuesCount = hideSnoozed ? filteredIssues.length : issues.length;

  return (
    <Badge badgeContent={loadingFiltered ? '...' : issuesCount }>
      <Icons.WarningRounded/>
    </Badge>
  );
}

const ACCOUNT_ORG_TYPES = [
  'account',
  'account-group',
  'department',
  'customer',
  'farm',
  'barn',
];

export default ({ apiRoot, organizationEntity, userEntity }) => {
  const links = [];
  const { isProducer, userFeatures } = useStore();
  const { features, loading: accountFeaturesLoading } = useAccountFeatures({ organizationId: organizationEntity?.getLink('self')?.href });
  const { entity: benchmarksEntity, loading: benchmarksLoading } = useSirenEntity({ href: organizationEntity?.getLink('https://api.binsentry.com/rel/benchmarks')?.href });
  const { millBinsGroup, loading: millBinsGroupLoading } = useMillBinsGroup();
  const { entity: indexEntity } = useSirenEntity({ href: apiRoot });
  const orgTypeEntity = organizationEntity?.getSubEntity('https://api.binsentry.com/rel/organization-type');
  const orgType = orgTypeEntity?.properties.name;
  const hasBins = organizationEntity?.getSubEntity('https://api.binsentry.com/rel/bins');
  const isAccount = ACCOUNT_ORG_TYPES.includes(orgType);
  const showOrdersFeatures = isAccount || isProducer && hasBins;
  const showAnimalGroups = organizationEntity?.hasLink('https://api.binsentry.com/rel/animal-groups');

  const { submenuLinks, submenuLinksLoading } = useSubmenuLinks({ organizationEntity, showOrdersFeatures });

  const loading = accountFeaturesLoading || submenuLinksLoading || benchmarksLoading || millBinsGroupLoading;
  if (!organizationEntity || !features || loading) {
    return { links };
  }

  if (!orgTypeEntity) {
    return { links };
  }

  const legacyBinsFeature = userFeatures?.legacyBinList || (isProducer && features.legacyBinList);

  const metricsUsedByInsights = ['outage-count', 'outage-duration', 'soft-outage-count', 'soft-outage-duration'];
  const hasMetrics = benchmarksEntity?.getSubEntities('https://api.binsentry.com/rel/metric').some(metric => metricsUsedByInsights.includes(metric.properties.metric));
  const showInsights = hasMetrics && (isAccount || isProducer);

  if (showOrdersFeatures) {
    links.push({
      translationKey: 'navigationLinkTitles.bins',
      icon: <BinIcon/>,
      href: '/customers',
      bottomBar: true,
    });

    const canViewOrders = organizationEntity.hasLink('https://api.binsentry.com/rel/search-orders');
    canViewOrders && links.push({
      translationKey: 'navigationLinkTitles.orderManager',
      icon: <Icons.Receipt/>,
      href: '/orders',
      bottomBar: true,
    });
  }

  if (showAnimalGroups) {
    links.push({
      translationKey: 'navigationLinkTitles.animalGroups',
      icon: <BarnIcon/>,
      href: '/animal-groups',
    });
    links.push({
      translationKey: 'navigationLinkTitles.feedLevels',
      icon: <Icons.Assessment/>,
      href: '/feed-levels',
    });
  }

  if (millBinsGroup && !isProducer) {
    links.push({
      translationKey: 'navigationLinkTitles.facilityInventory',
      icon: <MillBinIcon/>,
      href: '/inventory',
    });
  }

  if (showInsights) {
    links.push({
      translationKey: 'navigationLinkTitles.insights',
      icon: <InsightsIcon/>,
      href: '/insights',
    });
  }

  const showIssues = organizationEntity.hasLink('https://api.binsentry.com/rel/issues');
  const issuesHref = organizationEntity?.getLink('https://api.binsentry.com/rel/issues')?.href;
  if (showIssues) {
    links.push({
      translationKey: 'navigationLinkTitles.events',
      icon: <EventsBadge issuesHref={issuesHref} />,
      href: '/events',
      bottomBar: true,
    });
  }

  const firstSettingsSubMenuWithLinks = submenuLinks['navigationLinkTitles.settings'].find(submenu => submenu.links.length > 0);
  if (!isProducer && firstSettingsSubMenuWithLinks) {
    links.push({
      translationKey: 'navigationLinkTitles.settings',
      icon: <Icons.Settings/>,
      href: firstSettingsSubMenuWithLinks.links[0].href,
      hideInDrawer: true,
    });
  }

  if (legacyBinsFeature) {
    links.push('-');

    links.push({
      translationKey: 'navigationLinkTitles.binsLegacy',
      icon: <BinIcon/>,
      href: '/bins',
      bottomBar: true,
    });
  }

  if (orgType === 'sales-account') {
    links.push({
      translationKey: 'navigationLinkTitles.customers',
      href: '/related',
      icon: <Icons.PeopleAlt/>,
    });
  }

  const showDevices = organizationEntity.hasSubEntityByClass('devices') && ['dealer', 'service-account'].includes(orgType);

  if (showDevices) {
    links.push({
      translationKey: 'navigationLinkTitles.devices',
      icon: <Icons.Router/>,
      href: '/devices',
      bottomBar: true,
    });
  }

  if (orgType === 'service-account') {
    links.push({
      translationKey: 'navigationLinkTitles.review',
      icon: <Icons.RateReview/>,
      href: '/review',
    });
  }

  const workOrderLocationsLink = organizationEntity.getSubEntityByRel('https://api.binsentry.com/rel/work-order-locations');

  if (workOrderLocationsLink) {
    if (['service-account', 'service-partner'].includes(orgType)) {
      links.push({
        translationKey: 'navigationLinkTitles.workOrders',
        href: '/work-order-locations',
        icon: <Icons.Work/>,
        bottomBar: true,
      });
      links.push({
        translationKey: 'navigationLinkTitles.scheduler',
        href: '/scheduler',
        icon: <Icons.ListAlt/>,
        bottomBar: true,
      });
    }
  }

  const userWorkOrderLocationsLinks = userEntity?.getSubEntityByRel('https://api.binsentry.com/rel/work-order-locations');

  if (userWorkOrderLocationsLinks) {
    links.push({
      translationKey: 'navigationLinkTitles.mySchedule',
      bottomBar: true,
      href: '/work-order-schedule',
      icon: <Icons.Schedule/>,
    });
  }

  if (indexEntity?.getLink('https://api.binsentry.com/rel/billing')) {
    links.push({
      translationKey: 'navigationLinkTitles.billing',
      href: '/billing-management',
      icon: <Icons.AttachMoney/>,
    });
  }

  links.push({
    translationKey: 'navigationLinkTitles.helpCenter',
    href: 'https://knowledge.binsentry.com',
    icon: <Icons.Help/>,
    newWindow: true,
  });

  if (userFeatures?.deviceAnalysis && indexEntity?.getLink('https://api.binsentry.com/rel/device-analysis')) {
    links.push({
      translationKey: 'navigationLinkTitles.deviceAnalysis',
      href: '/device-analysis',
      icon: <Icons.MonitorHeart/>,
    });
  }
  if (userFeatures?.workOrderv2User) {
    links.push({
      translationKey: 'navigationLinkTitles.workOrders',
      href: '/work-orders',
      icon: <Icons.Work/>,
      bottomBar: true,
    });
  }

  const selectedDirectLink = links.find(link => link.translationKey && window.location.pathname.startsWith(link.href))?.translationKey;

  const selected = selectedDirectLink || _selectedSubLink(submenuLinks);

  return { links, submenuLinks, selected };
};

function _selectedSubLink(subLinks) {
  return Object.keys(subLinks).find(group =>
    subLinks[group].find(({ links }) => links
      .find(link => window.location.pathname.startsWith(link?.href)),
    ),
  );
}

function useSubmenuLinks({ organizationEntity, showOrdersFeatures }) {
  const { properties: orgTypeProperties } = organizationEntity?.getSubEntity('https://api.binsentry.com/rel/organization-type') || {};
  const showBilling = organizationEntity?.hasEntity('https://api.binsentry.com/rel/contract');
  const showFeedTemplates = organizationEntity?.hasLink('https://api.binsentry.com/rel/consumption-plans') && orgTypeProperties.name !== 'binsentry';

  const { entity: importsEntity, loading: importsLoading } = useSirenEntity({ href: organizationEntity?.getSubEntity('https://api.binsentry.com/rel/imports')?.href });
  const showBinImports = importsEntity?.hasLink('https://api.binsentry.com/rel/bin-imports');
  const showUserImports = importsEntity?.hasLink('https://api.binsentry.com/rel/user-imports');
  const showImports = showBinImports || showUserImports;

  const firstNotificationType = Object.keys(subscriptionTypeDetails)[0];
  const { entity: subscriptionDefaultsEntity, loading: subscriptionDefaultsLoading } = useSirenEntity({ href: organizationEntity?.getLink('https://api.binsentry.com/rel/subscription-defaults')?.href });
  const showCorporateNotificationDefaults = subscriptionDefaultsEntity?.hasAction(`add-${firstNotificationType}`) || subscriptionDefaultsEntity?.getSubEntityByClass(firstNotificationType)?.getAction(`update-${firstNotificationType}`);

  return {
    submenuLinks: {
      'navigationLinkTitles.notifications': [
        {
          translationKey: 'navigationLinkTitles.notifications',
          hideInDrawer: true,
          links: [
            { translationKey: 'navigationLinkTitles.inbox', href: '/notifications/inbox' },
            { translationKey: 'navigationLinkTitles.settings', href: '/notifications/settings' },
            { translationKey: 'navigationLinkTitles.customizeNotifications', href: '/notifications/customize' },
          ],
        },
      ],
      'navigationLinkTitles.settings': [
        {
          translationKey: 'navigationLinkTitles.settings',
          links: [
            { translationKey: 'navigationLinkTitles.organizations', href: '/organizations' },
            ...showOrdersFeatures ? [{ translationKey: 'navigationLinkTitles.feeds', href: '/feeds' }] : [],
            ...showFeedTemplates ? [{ translationKey: 'navigationLinkTitles.feedBudgets', href: '/feed-budgets/templates' }] : [],
            ...showCorporateNotificationDefaults ? [{ translationKey: 'navigationLinkTitles.organizationRecommendations', href: '/notifications/recommendations' }] : [],
          ],
        },
        ...showBilling ? [{
          translationKey: 'navigationLinkTitles.account',
          links: [{ translationKey: 'navigationLinkTitles.billing', href: '/billing' }],
        }] : [],
        ...showImports ? [{
          translationKey: 'navigationLinkTitles.imports',
          links: [
            ...showBinImports ? [{ translationKey: 'navigationLinkTitles.binImports', href: '/bin-imports' }] : [],
            ...showUserImports ? [{ translationKey: 'navigationLinkTitles.userImports', href: '/user-imports' }] : [],
          ],
        }] : [],
      ],
    },
    loading: importsLoading || subscriptionDefaultsLoading,
  };
}
